import { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { TenantProvider } from './context/TenantContext';
import { WebBookingBrandingProvider } from './context/WebBookingBrandingContext';
import { BookingPage } from './components/BookingPage';
import {
  WebPaymentCookiesPolicyPage,
  WebPaymentLegalNoticePage,
  WebPaymentPrivacyPolicyPage,
} from './components/LegalPages';
import { ThemeToggle } from './components/ThemeToggle';
import { BookingAppearanceSync } from './components/BookingAppearanceSync';
import { WebPaymentFooter } from './components/WebPaymentFooter';
import './App.css';

const PaymentPage = lazy(() => import('./components/PaymentPage').then(m => ({ default: m.PaymentPage })));
const PaymentResultPage = lazy(() => import('./components/PaymentResultPage').then(m => ({ default: m.PaymentResultPage })));
const BookingsPage = lazy(() => import('./components/BookingsPage').then(m => ({ default: m.BookingsPage })));
const CancelBookingPage = lazy(() => import('./components/CancelBookingPage').then(m => ({ default: m.CancelBookingPage })));
const RescheduleResponsePage = lazy(() => import('./components/RescheduleResponsePage').then(m => ({ default: m.RescheduleResponsePage })));
const SignQuotePage = lazy(() => import('./components/SignQuotePage').then(m => ({ default: m.SignQuotePage })));
const GymPage = lazy(() => import('./components/GymPage').then(m => ({ default: m.GymPage })));
const BundlesPage = lazy(() => import('./components/BundlesPage').then(m => ({ default: m.BundlesPage })));
const FoodtruckPage = lazy(() => import('./components/FoodtruckPage').then(m => ({ default: m.FoodtruckPage })));
const FoodtruckResultPage = lazy(() =>
  import('./components/FoodtruckResultPage').then(m => ({ default: m.FoodtruckResultPage })),
);
const RentalPage = lazy(() => import('./components/RentalPage').then(m => ({ default: m.RentalPage })));
const RentalCheckoutPage = lazy(() =>
  import('./components/RentalCheckoutPage').then(m => ({ default: m.RentalCheckoutPage })),
);
const RentalOrderStatusPage = lazy(() =>
  import('./components/RentalOrderStatusPage').then(m => ({ default: m.RentalOrderStatusPage })),
);
const CreatePaymentPage = lazy(() => import('./components/CreatePaymentPage').then(m => ({ default: m.CreatePaymentPage })));
const SignInPage = lazy(() => import('./components/Auth/SignInPage').then(m => ({ default: m.SignInPage })));
const JoinPage = lazy(() => import('./components/JoinPage').then(m => ({ default: m.JoinPage })));
const StartPage = lazy(() => import('./components/StartPage').then(m => ({ default: m.StartPage })));
const BusinessLandingPage = lazy(() =>
  import('./components/business-landing/BusinessLandingPage').then(m => ({ default: m.BusinessLandingPage })),
);
const ActivityPage = lazy(() =>
  import('./components/ActivityPage').then(m => ({ default: m.ActivityPage })),
);

/** Legacy: redirect /success, /payment/success, /payment/cancel, /cancel to /pay/:id/result */
function LegacyResultRedirect({ cancel = false }: { cancel?: boolean }) {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('payment_intent_id') || searchParams.get('payment_intent');
  const tenantId = searchParams.get('tenant_id');
  if (!id) return <Navigate to="/" replace />;
  const path = `/pay/${encodeURIComponent(id)}/result${cancel ? '?status=cancelled' : ''}`;
  const sep = path.includes('?') ? '&' : '?';
  const full = tenantId ? `${path}${sep}tenant_id=${encodeURIComponent(tenantId)}` : path;
  return <Navigate to={full} replace />;
}

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// ✅ Componente wrapper para Clerk (solo si está configurado)
/**
 * Sin esto, Clerk pinta su modal con los valores por defecto: tipografía ajena y, en móvil, una
 * tarjeta más ancha que el viewport que además queda inalcanzable porque `html, body` llevan
 * `overflow-x: hidden`. Se define en el provider para que lo herede todo el árbol.
 */
const CLERK_APPEARANCE = {
  variables: {
    colorPrimary: '#c05a36',
    borderRadius: '12px',
    fontFamily: "'DM Sans', -apple-system, 'Segoe UI', Arial, sans-serif",
  },
  elements: {
    // Deja que el ancho lo gobierne el CSS responsive de theme.css (.cl-*)
    rootBox: { width: '100%', maxWidth: '100%' },
    cardBox: { width: '100%', maxWidth: '100%' },
  },
} as const;

function AppWithClerk({ children }: { children: React.ReactNode }) {
  if (!CLERK_PUBLISHABLE_KEY) {
    // Si no hay clave de Clerk, renderizar sin ClerkProvider
    return <CustomerAuthProvider hayClerk={false}>{children}</CustomerAuthProvider>;
  }

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} appearance={CLERK_APPEARANCE}>
      {/*
        ⚠️ DENTRO del ClerkProvider, no fuera: es lo que permite que cualquier pantalla pida el
        token de sesión sin partirse en dos componentes según haya Clerk o no. Ver
        `CustomerAuthContext` — hasta ahora, las que no se partían simplemente no lo pasaban.
      */}
      <CustomerAuthProvider hayClerk>{children}</CustomerAuthProvider>
    </ClerkProvider>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();
  const hideFloatingTheme =
    pathname === '/booking' ||
    pathname.startsWith('/booking/') ||
    pathname === '/bookings' ||
    pathname.startsWith('/bookings/') ||
    pathname === '/reservations' ||
    pathname.startsWith('/reservations/') ||
    pathname === '/restaurant' ||
    pathname.startsWith('/restaurant/') ||
    pathname === '/gym' ||
    pathname.startsWith('/gym/') ||
    pathname === '/bundles' ||
    pathname.startsWith('/bundles/') ||
    pathname === '/foodtruck' ||
    pathname.startsWith('/foodtruck/') ||
    pathname === '/rental' ||
    pathname.startsWith('/rental/') ||
    pathname === '/join' ||
    pathname.startsWith('/join/') ||
    pathname === '/start' ||
    pathname.startsWith('/start/') ||
    // Business landing pages handle their own UI chrome
    (!pathname.startsWith('/pay') &&
      !pathname.startsWith('/payment') &&
      !pathname.startsWith('/bookings') &&
      !pathname.startsWith('/reservations') &&
      !pathname.startsWith('/sign-in') &&
      !pathname.startsWith('/cancel') &&
      !pathname.startsWith('/reschedule') &&
      !pathname.startsWith('/payments') &&
      !pathname.startsWith('/aviso') &&
      !pathname.startsWith('/legal') &&
      !pathname.startsWith('/privacidad') &&
      !pathname.startsWith('/politica') &&
      !pathname.startsWith('/cookies') &&
      pathname !== '/' &&
      /^\/[a-z0-9-]+$/.test(pathname));

  return (
    <>
      {!hideFloatingTheme && <ThemeToggle floating />}
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><div className="spinner" /></div>}>
      <Routes>
        <Route path="/pay" element={<PaymentPage />} />
        <Route path="/pay/:paymentIntentId" element={<PaymentPage />} />
        <Route path="/pay/:paymentIntentId/result" element={<PaymentResultPage />} />
        <Route path="/payments/create" element={<CreatePaymentPage />} />
        <Route path="/payment/success" element={<LegacyResultRedirect />} />
        <Route path="/payment/cancel" element={<LegacyResultRedirect cancel />} />
        <Route path="/success" element={<LegacyResultRedirect />} />
        <Route path="/cancel" element={<LegacyResultRedirect cancel />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/reservations" element={<BookingsPage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/restaurant" element={<BookingPage />} />
        <Route path="/cancel-booking" element={<CancelBookingPage />} />
        <Route path="/reschedule-accept" element={<RescheduleResponsePage />} />
        <Route path="/reschedule-reject" element={<RescheduleResponsePage />} />
        {/* FIRMAR: el cliente ve su presupuesto y lo acepta o lo rechaza. El token va en la
            query, igual que en los enlaces de cancelación. */}
        <Route path="/firmar" element={<SignQuotePage />} />
        <Route path="/gym" element={<GymPage />} />
        <Route path="/bundles" element={<BundlesPage />} />
        <Route path="/foodtruck" element={<FoodtruckPage />} />
        <Route path="/foodtruck/result" element={<FoodtruckResultPage />} />
        <Route path="/rental" element={<RentalPage />} />
        <Route path="/rental/checkout" element={<RentalCheckoutPage />} />
        <Route path="/rental/order/:webSessionId" element={<RentalOrderStatusPage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/start" element={<StartPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/aviso-legal" element={<WebPaymentLegalNoticePage />} />
        <Route path="/legal" element={<WebPaymentLegalNoticePage />} />
        <Route path="/politica-de-privacidad" element={<WebPaymentPrivacyPolicyPage />} />
        <Route path="/privacidad" element={<WebPaymentPrivacyPolicyPage />} />
        <Route path="/politica-de-cookies" element={<WebPaymentCookiesPolicyPage />} />
        <Route path="/cookies" element={<WebPaymentCookiesPolicyPage />} />
        <Route path="/" element={<Navigate to="/booking" replace />} />
        {/* Public business landing pages — must be last to avoid colliding with static routes above */}
        <Route path="/:slug/activity" element={<ActivityPage />} />
        <Route path="/:slug" element={<BusinessLandingPage />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <AppWithClerk>
      <TenantProvider>
        <Router>
          <WebBookingBrandingProvider>
            <div className="App">
              <BookingAppearanceSync />
              <main className="App-main">
                <AppRoutes />
              </main>
              <WebPaymentFooter />
            </div>
          </WebBookingBrandingProvider>
        </Router>
      </TenantProvider>
    </AppWithClerk>
  );
}

export default App; 